import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { facilityAdmin } from "../../api";
import { useAuth } from "../../context/AuthContext";
import {
  PageHeader, Btn, Badge, EmptyState, LoadingSpinner,
  ConfirmModal, FormField, inputStyle, selectStyle, TempPasswordBox,
} from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";

export default function StaffPage() {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [staff,    setStaff]   = useState([]);
  const [stations, setStations]= useState([]);
  const [loading,  setLoading] = useState(true);
  const [search,   setSearch]  = useState("");
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [canImpersonate, setCanImpersonate]   = useState(false);

  useEffect(() => {
    // Per-facility now, not a platform-wide switch — reads this facility's
    // own FacilitySettings via the same endpoint the rest of this page
    // already uses, rather than a separate platform-level call.
    facilityAdmin.getSettings()
      .then(r => setCanImpersonate(!!r.data?.facility_admin_can_impersonate_staff))
      .catch(() => {});
  }, []);

  const handleImpersonate = async (member) => {
    setImpersonatingId(member.id);
    try {
      await impersonate(member.id);
      toast.success(`Viewing as ${member.name}.`);
      navigate("/staff");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to impersonate staff member.");
    } finally {
      setImpersonatingId(null);
    }
  };

  const [staffModal,  setStaffModal]  = useState(null);  // null | "create" | user
  const [resetModal,  setResetModal]  = useState(null);  // user
  const [confirmModal,setConfirmModal]= useState(null);  // { type, user }
  const [saving,      setSaving]      = useState(false);
  const [resetResult, setResetResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, str] = await Promise.all([
        facilityAdmin.listStaff(),
        facilityAdmin.listStations(),
      ]);
      setStaff(sr.data?.results || sr.data);
      setStations(str.data?.results || str.data);
    } catch { toast.error("Failed to load staff."); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = staff.filter(u =>
    !search ||
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  // ── Save staff ─────────────────────────────────────────────────────────────
  const saveStaff = async (form) => {
    setSaving(true);
    try {
      if (form.id) {
        await facilityAdmin.updateStaff(form.id, {
          name: form.name, station: form.station || null,
        });
        toast.success("Staff updated.");
      } else {
        await facilityAdmin.createStaff({
          name: form.name, email: form.email,
          password: form.password, role: "staff",
          station: form.station || null,
          must_change_password: true,
        });
        toast.success(`${form.name} added.`);
      }
      setStaffModal(null); load();
    } catch (e) {
      const msg = e.response?.data?.email?.[0] || e.response?.data?.detail || "Failed to save.";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  // ── Reset password ─────────────────────────────────────────────────────────
  const [newPw, setNewPw]   = useState("");
  const [pwMode, setPwMode] = useState("auto");

  const handleResetPw = async () => {
    setSaving(true);
    try {
      const pw = pwMode === "auto" ? genPw() : newPw;
      await facilityAdmin.resetStaffPassword(resetModal.id, { password: pw });
      setResetResult({ password: pw, email: resetModal.email });
      setResetModal(null);
      toast.success("Password reset.");
    } catch { toast.error("Failed to reset password."); }
    finally  { setSaving(false); }
  };

  // ── Deactivate / Reactivate ────────────────────────────────────────────────
  const handleConfirm = async () => {
    setSaving(true);
    try {
      if (confirmModal.type === "deactivate") await facilityAdmin.deactivateStaff(confirmModal.user.id);
      else                                     await facilityAdmin.reactivateStaff(confirmModal.user.id);
      toast.success(`${confirmModal.user.name} ${confirmModal.type === "deactivate" ? "deactivated" : "reactivated"}.`);
      setConfirmModal(null); load();
    } catch { toast.error("Action failed."); }
    finally  { setSaving(false); }
  };

  return (
    <div style={styles.root}>
      <PageHeader
        title="Staff"
        subtitle={`${staff.length} staff member${staff.length !== 1 ? "s" : ""} at this facility`}
        action={<Btn onClick={() => setStaffModal("create")}>+ Add Staff</Btn>}
      />

      <div style={styles.toolbar}>
        <input style={styles.search} placeholder="Search staff…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? <LoadingSpinner /> : filtered.length === 0 ? (
        <EmptyState icon="👤" title="No staff yet"
          subtitle="Add your first staff member to get started."
          action={<Btn onClick={() => setStaffModal("create")}>+ Add Staff</Btn>}
        />
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead><tr>
              {["Name", "Email", "Station", "Status", "Actions"].map(h => <th key={h} style={styles.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.userRow}>
                      <div style={styles.avatar}>{u.name?.[0]?.toUpperCase()}</div>
                      <div>
                        <div style={styles.userName}>
                          {u.name}
                          {u.must_change_password && <span title="Must change password" style={styles.pwIcon}>🔑</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}><span style={styles.email}>{u.email}</span></td>
                  <td style={styles.td}>
                    <span style={{ fontSize: "13px", color: u.station_name ? "var(--text-secondary)" : "var(--text-faint)" }}>
                      {u.station_name || "Unassigned"}
                    </span>
                  </td>
                  <td style={styles.td}><Badge value={u.is_active ? "active" : "inactive"} /></td>
                  <td style={styles.td}>
                    <div style={styles.actions}>
                      {canImpersonate && u.is_active && (
                        <Btn size="sm" variant="ghost"
                          onClick={() => handleImpersonate(u)}
                          disabled={impersonatingId === u.id}>
                          {impersonatingId === u.id ? "Loading…" : "Impersonate"}
                        </Btn>
                      )}
                      <Btn size="sm" variant="ghost" onClick={() => setStaffModal({ ...u, station: u.station || "" })}>Edit</Btn>
                      <Btn size="sm" variant="ghost" onClick={() => { setResetModal(u); setPwMode("auto"); setNewPw(""); }}>Reset PW</Btn>
                      {u.is_active
                        ? <Btn size="sm" variant="danger" onClick={() => setConfirmModal({ type: "deactivate", user: u })}>Deactivate</Btn>
                        : <Btn size="sm" variant="success" onClick={() => setConfirmModal({ type: "reactivate", user: u })}>Reactivate</Btn>
                      }
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Add/Edit Staff Modal ── */}
      <StaffFormModal
        open={!!staffModal} onClose={() => setStaffModal(null)}
        initial={staffModal !== "create" ? staffModal : null}
        stations={stations} onSave={saveStaff} saving={saving}
      />

      {/* ── Reset Password Modal ── */}
      <Modal open={!!resetModal} onClose={() => setResetModal(null)} title="Reset Password" width="420px">
        {resetModal && (
          <div style={F.form}>
            <div style={F.userPrev}>
              <div style={styles.avatar}>{resetModal.name?.[0]?.toUpperCase()}</div>
              <div>
                <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--text-primary)" }}>{resetModal.name}</div>
                <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{resetModal.email}</div>
              </div>
            </div>
            <FormField label="Method">
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={F.radio}><input type="radio" checked={pwMode === "auto"} onChange={() => setPwMode("auto")} /><span style={{ color: "var(--text-primary)", fontSize: "13px" }}>Auto-generate temp password</span></label>
                <label style={F.radio}><input type="radio" checked={pwMode === "manual"} onChange={() => setPwMode("manual")} /><span style={{ color: "var(--text-primary)", fontSize: "13px" }}>Set manually</span></label>
              </div>
            </FormField>
            {pwMode === "manual" && (
              <FormField label="New Password"><input style={inputStyle} type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Min. 8 characters" /></FormField>
            )}
            <div style={F.footer}>
              <Btn variant="ghost" onClick={() => setResetModal(null)} disabled={saving}>Cancel</Btn>
              <Btn onClick={handleResetPw} disabled={saving || (pwMode === "manual" && newPw.length < 8)}>{saving ? "Resetting…" : "Reset"}</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Temp Password Result ── */}
      <Modal open={!!resetResult} onClose={() => setResetResult(null)} title="Password Reset" width="420px">
        {resetResult && (
          <div style={F.form}>
            <TempPasswordBox password={resetResult.password} email={resetResult.email} />
            <div style={F.footer}><Btn onClick={() => setResetResult(null)}>Done</Btn></div>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={!!confirmModal} onClose={() => setConfirmModal(null)}
        onConfirm={handleConfirm} loading={saving}
        title={confirmModal?.type === "deactivate" ? "Deactivate Staff?" : "Reactivate Staff?"}
        message={confirmModal?.type === "deactivate"
          ? `${confirmModal?.user?.name} will lose access immediately.`
          : `${confirmModal?.user?.name} will regain access.`}
        confirmLabel={confirmModal?.type === "deactivate" ? "Deactivate" : "Reactivate"}
        variant={confirmModal?.type === "deactivate" ? "danger" : "success"}
      />
    </div>
  );
}

function StaffFormModal({ open, onClose, initial, stations, onSave, saving }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", station: "" });
  useEffect(() => {
    if (open) setForm(initial ? { name: initial.name, email: initial.email, station: initial.station || "", password: "" } : { name: "", email: "", password: "", station: "" });
  }, [open, initial]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const isEdit = !!initial;
  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Edit Staff" : "Add Staff Member"} width="460px">
      <div style={F.form}>
        <FormField label="Full Name" required><input style={inputStyle} value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Abena Mensah" /></FormField>
        {!isEdit && (
          <>
            <FormField label="Email" required><input style={inputStyle} type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="staff@hospital.com" /></FormField>
            <FormField label="Temporary Password" required hint="Staff will be forced to change on first login"><input style={inputStyle} type="password" value={form.password} onChange={e => set("password", e.target.value)} placeholder="Min. 8 characters" /></FormField>
          </>
        )}
        <FormField label="Assign Station" hint="Staff sees this station's queue when they log in">
          <select style={selectStyle} value={form.station} onChange={e => set("station", e.target.value)}>
            <option value="">— Unassigned —</option>
            {stations.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </FormField>
        <div style={F.footer}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={() => onSave({ ...form, id: initial?.id })} disabled={saving || !form.name.trim() || (!isEdit && (!form.email.trim() || form.password.length < 8))}>{saving ? "Saving…" : isEdit ? "Save Changes" : "Add Staff"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function genPw() {
  const c = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$";
  return Array.from({ length: 12 }, () => c[Math.floor(Math.random() * c.length)]).join("");
}

const styles = {
  root: { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1000px" },
  toolbar: { marginBottom: "20px" },
  search: { background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: "8px", padding: "9px 14px", color: "var(--text-primary)", fontSize: "14px", outline: "none", width: "280px", fontFamily: "inherit" },
  tableWrap: { background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "14px", overflow: "hidden" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { padding: "12px 16px", fontSize: "11px", fontWeight: "700", color: "var(--text-muted)", letterSpacing: "0.07em", textTransform: "uppercase", textAlign: "left", borderBottom: "1px solid var(--surface-border)", background: "var(--table-header-bg)" },
  tr: { borderBottom: "1px solid rgba(37,99,235,0.06)" },
  td: { padding: "13px 16px", verticalAlign: "middle" },
  userRow: { display: "flex", alignItems: "center", gap: "10px" },
  avatar: { width: "30px", height: "30px", borderRadius: "7px", background: "linear-gradient(135deg,#2563EB,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "700", color: "#fff", flexShrink: 0 },
  userName: { fontSize: "14px", fontWeight: "600", color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "5px" },
  email: { fontSize: "12px", color: "var(--text-muted)" },
  pwIcon: { fontSize: "12px" },
  actions: { display: "flex", gap: "6px", flexWrap: "wrap" },
};

const F = {
  form: { display: "flex", flexDirection: "column", gap: "14px" },
  footer: { display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "8px", borderTop: "1px solid var(--surface-border)", marginTop: "4px" },
  radio: { display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" },
  userPrev: { display: "flex", alignItems: "center", gap: "12px", background: "var(--input-bg)", borderRadius: "8px", padding: "10px 12px" },
};
